// Client-side mock pricing for the marketing configurator island (M11). Deliberately
// self-contained: marketing must NEVER call the live Framing API, so these are static
// figures. The per-size glazing/mat/print/shipping numbers MIRROR PricingExamples
// (app/models/pricing_examples.rb) so /try, the homepage teaser and /pricing never quote
// the same spec at two different prices — the default 30×40 oak/museum below resolves to
// €41.87, the exact teaser total.
//
// 🔴 Illustrative mock figures — confirm against the real Framing-API price model before
// the launch cutover, and keep them in sync with pricing_examples.rb.

export const VAT_RATE = 0.21; // Latvia standard rate (the operator's reference case).

// print/ship/glaze/mat are EUR cents. glaze holds the per-size cost of each glazing
// option; mat is the 8 cm mat cost. ratio drives the preview aspect.
export const SIZES = [
  { id: "21x30", label: "21 × 30 cm", ratio: 21 / 30, print: 1890, ship: 590, glaze: { acrylic: 210, museum: 250 }, mat: 520 },
  { id: "30x40", label: "30 × 40 cm", ratio: 30 / 40, print: 2450, ship: 690, glaze: { acrylic: 240, museum: 320 }, mat: 640 },
  { id: "50x70", label: "50 × 70 cm", ratio: 50 / 70, print: 4280, ship: 990, glaze: { acrylic: 620, museum: 860 }, mat: 940 },
  { id: "70x100", label: "70 × 100 cm", ratio: 70 / 100, print: 6890, ship: 1490, glaze: { acrylic: 1040, museum: 1420 }, mat: 1480 },
];

// tone drives the preview frame colour. surcharge is added to print+framing.
export const MOULDINGS = [
  { id: "oak", label: "Black oak", tone: "#2e2b28", surcharge: 0 },
  { id: "ash", label: "Natural ash", tone: "#cbb894", surcharge: 0 },
  { id: "walnut", label: "Walnut", tone: "#4a2f22", surcharge: 600, paid: true },
];

// Glazing/mat prices live per-size on SIZES (above); these carry only the labels and the
// Paid-tier flag. The museum glass and the mat are Paid-tier unlocks.
export const GLAZINGS = [
  { id: "acrylic", label: "Acrylic glazing" },
  { id: "museum", label: "Museum glass", paid: true },
];

export const MATS = [
  { id: "none", label: "No mat" },
  { id: "mat", label: "8 cm mat", paid: true },
];

export const DEFAULT_SELECTION = { size: "30x40", moulding: "oak", glazing: "museum", mat: "none" };

const byId = (list, id) => list.find((row) => row.id === id) || list[0];

export function resolveSelection({ size, moulding, glazing, mat } = {}) {
  return {
    size: byId(SIZES, size),
    moulding: byId(MOULDINGS, moulding),
    glazing: byId(GLAZINGS, glazing),
    mat: byId(MATS, mat),
  };
}

// Returns integer EUR cents throughout: { lines: [[label, cents]...], subtotal, vat, total }.
export function priceBreakdown(selection) {
  const { size, moulding, glazing, mat } = resolveSelection(selection);
  const lines = [["Print + framing", size.print + moulding.surcharge]];
  if (mat.id !== "none") lines.push(["Mat", size.mat]);
  lines.push(["Glazing", size.glaze[glazing.id]]);
  lines.push(["Shipping (EU)", size.ship]);
  const subtotal = lines.reduce((sum, [, cents]) => sum + cents, 0);
  const vat = Math.round(subtotal * VAT_RATE);
  return { lines, subtotal, vat, total: subtotal + vat };
}

export function formatEuros(cents) {
  return `€${(Number(cents) / 100).toFixed(2)}`;
}
